/**
 * Bank reconciliation — do the books agree with the bank, account by account?
 *
 * In scope since 2026-09-13, the operator's decision. The rules live in
 * migration 0028 and src/services/bank-reconciliations.ts; this screen shows
 * them and never decides one.
 *
 * THREE BALANCES, BECAUSE TWO WOULD NOT SAY WHICH HALF IS WRONG
 *
 *   Statement  typed from the bank's statement
 *   Bank Feed  what the sync holds for the account through the date
 *   Books      posted lines through the date
 *
 * Statement against Bank Feed is the sync's completeness. Bank Feed against
 * Books is the coding backlog, and the lists below it name every item.
 *
 * SIGNS. The API speaks ledger sign, debit minus credit. The operator reads a
 * card statement as "$500 owed", which is -50000 on the credit-normal card. So
 * every amount on this screen is shown, and typed, in the account's NORMAL sign,
 * and converted in exactly two functions below. Nowhere else flips a sign.
 *
 * MARK RECONCILED is enabled only when the difference is zero, and the server
 * refuses otherwise anyway (the trigger is the rule). Undo is offered on the
 * latest reconciliation only, with the reason the Matching screen asks for.
 *
 * THE SHAPE OF THIS SCREEN, REARRANGED 2026-09-18 AT THE OPERATOR'S REQUEST
 *
 * Two questions, wanting opposite shapes. "Which months have I not done" is a
 * wide table read top to bottom; "reconcile THIS month" is a form about one
 * month. Stacked, the form came first and pushed the table below the fold, so
 * the screen opened on a form for a month nobody had picked, and the thing that
 * says what to work on was what you had to scroll for.
 *
 * So the table is the screen, and the form is a DRAWER over it, opened from a
 * row. The table keeps its place while the drawer is open, which is the point:
 * the month being reconciled is still visible in the list it came from.
 *
 * ONE ACCOUNT SELECTION, AND IT LIVES HERE. The `Account` select is gone; the
 * table's own tabs set it. Two pickers a few centimetres apart could disagree,
 * and did -- the history could show one account while the table below showed
 * another, with nothing on screen saying so.
 */

import { AccessNotice } from '../components/AccessNotice.js';
import { useCan } from '../auth/AuthProvider.js';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Toast } from '../components/Toast.js';
import { FileDropzone } from '../components/FileDropzone.js';
import { StatementMonths } from '../components/StatementMonths.js';
import { SideDrawer } from '../components/SideDrawer.js';
import { PaperclipIcon } from '../components/PaperclipIcon.js';
import { DifferenceExplained } from '../components/DifferenceExplained.js';
import { ReceiptOverlay } from '../components/ReceiptOverlay.js';
import { formatAmountInput, formatCents, parseCents } from '../lib/money.js';
import {
  ApiError,
  api,
  uploadToStorage,
  type BankStatement,
  type ReconcilableAccount,
  type Reconciliation,
  type ReconciliationPreview,
} from '../api.js';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Ledger sign to what the operator reads for this account. */
function toDisplay(ledgerCents: number, account: ReconcilableAccount): number {
  return account.normalBalance === 'credit' ? -ledgerCents : ledgerCents;
}

/** What the operator typed, in the account's normal sign, to ledger sign. Null when unparseable. */
function toLedger(text: string, account: ReconcilableAccount): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const negative = trimmed.startsWith('-');
  const cents = parseCents(negative ? trimmed.slice(1) : trimmed);
  if (cents === null) return null;
  const typed = negative ? -cents : cents;
  return account.normalBalance === 'credit' ? -typed : typed;
}

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

export function ReconciliationPage() {
  const mayReconcile = useCan()('reconcile');
  const [accounts, setAccounts] = useState<ReconcilableAccount[] | null>(null);
  const [accountId, setAccountId] = useState('');
  const [history, setHistory] = useState<Reconciliation[]>([]);
  /** The account's statements, so a date and a history row can show theirs. */
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [endDate, setEndDate] = useState('');
  const [balanceText, setBalanceText] = useState('');
  const [preview, setPreview] = useState<ReconciliationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [undoing, setUndoing] = useState<{ id: string; reason: string } | null>(null);
  /** Bumped after a write, so the account list, history and preview re-read. */
  const [generation, setGeneration] = useState(0);
  /** Open when the operator is reconciling a month. Closed is the resting state. */
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** The statement being read, in the overlay every other PDF uses. */
  const [viewing, setViewing] = useState<BankStatement | null>(null);
  /*
   * FOLDED TO START. The months table above answers "what do I still have to
   * do"; the history answers "what did I already do", which is the rarer
   * question and was taking a screen's worth of room above the fold to answer
   * it. The summary line carries the count and the latest date, so nothing
   * about the state of the account is behind the click -- only the rows.
   */
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listReconcilableAccounts()
      .then((result) => {
        if (cancelled) return;
        setAccounts(result.accounts);
        setAccountId((current) => current || result.accounts[0]?.accountId || '');
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(message(caught, 'Could not read the bank accounts.'));
      });
    return () => {
      cancelled = true;
    };
  }, [generation]);

  const account = accounts?.find((candidate) => candidate.accountId === accountId);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    api
      .listReconciliations(accountId)
      .then((result) => {
        if (!cancelled) setHistory(result.reconciliations);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(message(caught, 'Could not read the reconciliations.'));
      });
    api
      .listStatements(accountId)
      .then((result) => {
        if (!cancelled) setStatements(result.statements);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(message(caught, 'Could not read the statements.'));
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, generation]);

  const statementLedger = account ? toLedger(balanceText, account) : null;
  const balanceInvalid = balanceText.trim() !== '' && statementLedger === null;

  useEffect(() => {
    setPreview(null);
    if (!accountId || !DATE.test(endDate) || balanceInvalid) return;
    let cancelled = false;
    api
      .previewReconciliation(accountId, endDate, statementLedger ?? undefined)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(message(caught, 'Could not compare that date.'));
      });
    return () => {
      cancelled = true;
    };
    // statementLedger is derived from balanceText and account; listing both
    // would re-run on every render of an unchanged value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, endDate, balanceText, balanceInvalid, generation]);

  async function markReconciled() {
    if (!account || statementLedger === null) return;
    setBusy(true);
    setError(null);
    try {
      const { reconciliation } = await api.completeReconciliation({
        accountId: account.accountId,
        statementEndDate: endDate,
        statementBalanceCents: statementLedger,
      });
      setNotice(`${account.code} ${account.name} reconciled through ${reconciliation.statementEndDate}`);
      setEndDate('');
      setBalanceText('');
      /*
       * The drawer closes so the result is VISIBLE. Its own success message
       * would sit on a panel covering the table that has just changed, and the
       * operator's next question is "did the month tick over" -- which is
       * answered by the row behind it. `generation` is what makes that row
       * re-read; without it the table kept its old answer and the screen
       * contradicted itself (reported 2026-09-18).
       */
      setDrawerOpen(false);
      setGeneration((n) => n + 1);
    } catch (caught) {
      setError(message(caught, 'Could not record the reconciliation.'));
    } finally {
      setBusy(false);
    }
  }

  /*
   * The bank's statement for this account and date: presign, POST to storage,
   * record -- the receipt sequence, through the shared drop zone. One per date.
   */
  async function uploadStatement(file: File) {
    if (!account || !DATE.test(endDate)) return;
    const body = {
      accountId: account.accountId,
      statementEndDate: endDate,
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    };
    const { upload } = await api.presignStatement(body);
    await uploadToStorage(upload, file);
    const { statement } = await api.recordStatement({ ...body, key: upload.key });
    setNotice(`Attached ${statement.originalFilename}`);
    setGeneration((n) => n + 1);
  }

  /**
   * THE OVERLAY, NOT A NEW TAB, and this is the third time that has had to be
   * said on this screen.
   *
   * An inline URL is for a FRAME. Opening one with `window.open` works well
   * enough to pass review and throws away everything the overlay knows: the
   * `inline` disposition, the `frame-src` policy, and the browser setting that
   * turns a frame into a download and then blames the CSP -- three gates that
   * cost a whole session to find once, and which are written down in
   * `ReceiptOverlay`'s own header so nobody has to find them again.
   *
   * `attachment-viewer.test.ts` fails if any screen asks for an `inline` URL
   * and calls `window.open`, because noticing this by reading was not working.
   */
  function openStatement(statement: BankStatement) {
    setViewing(statement);
  }

  async function removeStatement(statement: BankStatement) {
    setBusy(true);
    setError(null);
    try {
      await api.removeStatement(statement.id);
      setNotice(`Removed ${statement.originalFilename}`);
      setGeneration((n) => n + 1);
    } catch (caught) {
      setError(message(caught, 'Could not remove that statement.'));
    } finally {
      setBusy(false);
    }
  }

  async function undo() {
    if (!undoing || undoing.reason.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      await api.voidReconciliation(undoing.id, undoing.reason.trim());
      setUndoing(null);
      setNotice('Reconciliation undone');
      setGeneration((n) => n + 1);
    } catch (caught) {
      setError(message(caught, 'Could not undo that reconciliation.'));
    } finally {
      setBusy(false);
    }
  }

  if (accounts === null) {
    return error ? (
      <p role="alert" className="error">
        {error}
      </p>
    ) : (
      <p className="muted">Loading…</p>
    );
  }
  if (accounts.length === 0) {
    return (
      <section className="reconcile">
        <h1>Reconcile</h1>
        <p className="muted">There are no bank or card accounts to reconcile.</p>
      </section>
    );
  }

  const show = (ledgerCents: number) => (account ? formatCents(toDisplay(ledgerCents, account)) : '');
  const latestLive = history.find((row) => row.voidedAt === null);
  const statementFor = (date: string) => statements.find((row) => row.statementEndDate === date);

  /**
   * Fills this form from a row of the months table, and says it did.
   *
   * WHAT IS AUTOMATED IS THE TRANSCRIPTION, NOT THE JUDGEMENT. Before this the
   * operator opened a statement PDF, found the closing balance, and typed it
   * into a field on the same screen that already held the number -- Mercury
   * reports it with the statement and migration 0040 stores it. So the form is
   * filled in and nothing is submitted: the three balances still have to agree
   * before Mark Reconciled enables, and the operator still presses it. §6.5.
   *
   * THE ACCOUNT TRAVELS WITH THE ROW. The table keeps its own tab, so a row
   * clicked under 1020 must move this form to 1020 as well; without that the
   * September figure from one account would be drafted against another, and
   * every balance on the screen would then be right about the wrong thing.
   */
  function draftFrom(input: {
    accountId: string;
    monthEnd: string;
    statementBalanceCents: number | null;
  }) {
    const target = accounts?.find((candidate) => candidate.accountId === input.accountId);
    if (target === undefined) return;
    setError(null);
    setAccountId(input.accountId);
    setEndDate(input.monthEnd);
    /*
     * BLANK WHERE THE BANK'S FIGURE IS UNKNOWN, and the drawer opens anyway.
     * A month with no statement is exactly the one that needs opening: the
     * drawer is where the PDF is attached and the balance typed. Filling the
     * field with a zero would be the screen making an assertion nobody made.
     */
    setBalanceText(
      input.statementBalanceCents === null
        ? ''
        : // The account's normal sign, exactly as the operator would have typed
          // it -- `toDisplay` is the conversion the rest of the screen uses.
          formatAmountInput(toDisplay(input.statementBalanceCents, target)),
    );
    setNotice(
      input.statementBalanceCents === null
        ? null
        : `Drafted from the statement for ${input.monthEnd}. Check the balances, then mark it reconciled.`,
    );
    setDrawerOpen(true);
  }
  const currentStatement = DATE.test(endDate) ? statementFor(endDate) : undefined;
  const difference = preview?.differenceCents ?? null;
  const feedGap =
    preview && preview.feedBalanceCents !== null && preview.statementBalanceCents !== null
      ? preview.statementBalanceCents - preview.feedBalanceCents
      : null;

  return (
    <section className="reconcile">
      <h1>Reconcile</h1>
    <AccessNotice capability="reconcile" what="reconciliations" />

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {error && !drawerOpen && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {/*
        THE TABLE IS THE SCREEN. It answers "which months have I not done",
        which is the question the operator arrives with; the form that answers
        "reconcile this one" is a drawer, opened from a row. Its tabs are the
        only account selection on the screen.
      */}
      <StatementMonths
        accounts={accounts}
        accountId={accountId}
        onAccountChange={(next) => {
          setAccountId(next);
          setUndoing(null);
          setError(null);
        }}
        refreshKey={generation}
        // No Reconcile on a month for a session that may not: the drawer is its only way in.
        onDraft={mayReconcile ? draftFrom : undefined}
      />

      {account && (
        <p role="status" className={`notice ${account.reconciledThrough ? 'notice-posted' : ''}`}>
          {account.reconciledThrough
            ? `Reconciled through ${account.reconciledThrough} at ${show(account.reconciledBalanceCents ?? 0)}`
            : 'Never reconciled'}
        </p>
      )}

      {history.length > 0 && account && (
        <section className="reconcile-history" aria-label="Reconciliation History">
          {/*
            The whole line is the target rather than a chevron at the end of it,
            which is the shape the Home screen's queue warning uses.
          */}
          <button
            type="button"
            className="home-health-toggle"
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((open) => !open)}
          >
            <span>
              <strong>History</strong>{' '}
              <span className="muted">
                {history.length} {history.length === 1 ? 'reconciliation' : 'reconciliations'}
                {latestLive && `, latest through ${latestLive.statementEndDate}`}
              </span>
            </span>
            <span className="home-health-chevron" aria-hidden="true">
              {historyOpen ? '▲' : '▼'}
            </span>
          </button>
          {historyOpen && (
          <table className="lines">
            <thead>
              <tr>
                <th>Through</th>
                <th className="num">Balance</th>
                {/*
                  ITS OWN COLUMN, AND THE SAME WORD AS THE TABLE ABOVE. The link
                  used to sit inside the Reconciled By cell, so the one piece of
                  evidence a reader might want to check was buried in a cell
                  about who did it -- and a reader scanning for statements found
                  a column of email addresses.
                */}
                <th className="statement-cell">Statement</th>
                <th>Reconciled By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.map((row) => {
                const evidence = statementFor(row.statementEndDate);
                return (
                  <tr key={row.id} className={row.voidedAt ? 'muted' : undefined}>
                    <td className="date-cell">{row.statementEndDate}</td>
                    <td className="num">{show(row.statementBalanceCents)}</td>
                    <td className="statement-cell">
                      {evidence && (
                        <button
                          type="button"
                          className="icon-button"
                          title={evidence.originalFilename}
                          aria-label={`Open the statement through ${row.statementEndDate}`}
                          onClick={() => void openStatement(evidence)}
                        >
                          <PaperclipIcon />
                        </button>
                      )}
                    </td>
                    <td>
                      {row.createdByEmail ?? 'No user recorded'}
                      {row.voidedAt && ` · Undone: ${row.voidedReason}`}
                    </td>
                    <td>
                      {mayReconcile && row.id === latestLive?.id && (
                        <button
                          type="button"
                          className="link"
                          aria-label={`Undo the reconciliation through ${row.statementEndDate}`}
                          onClick={() => setUndoing({ id: row.id, reason: '' })}
                        >
                          Undo
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
          {historyOpen && undoing && (
            <div className="new-account undo-form">
              <label className="field">
                <span>Reason</span>
                <input
                  aria-label="Reason for undoing the reconciliation"
                  value={undoing.reason}
                  onChange={(event) => setUndoing({ ...undoing, reason: event.target.value })}
                />
              </label>
              <div className="row">
                <button
                  type="button"
                  disabled={busy || undoing.reason.trim() === ''}
                  onClick={() => void undo()}
                >
                  Undo the Reconciliation
                </button>
                <button type="button" className="link" onClick={() => setUndoing(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/*
        THE SAME MODAL RECEIPTS USE, rendered at the page level so it sits above
        the drawer as well as the tables. `resolveUrl` is the only difference: a
        statement's signed URL comes from its own endpoint.
      */}
      {viewing !== null && (
        <ReceiptOverlay
          label="Statement"
          attachment={
            {
              id: viewing.id,
              originalFilename: viewing.originalFilename,
              contentType: viewing.contentType,
              sizeBytes: viewing.sizeBytes,
            } as never
          }
          resolveUrl={(id, disposition) => api.statementUrl(id, disposition)}
          onClose={() => setViewing(null)}
        />
      )}

      {drawerOpen && account && (
        <SideDrawer
          label={`Reconcile ${account.code} ${account.name}`}
          onClose={() => setDrawerOpen(false)}
        >
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}

          <div className="row reconcile-form">
            <label className="field">
              <span>Statement End Date</span>
              <input
                type="date"
                value={endDate}
                onChange={(event) => {
                  setError(null);
                  setEndDate(event.target.value);
                }}
              />
            </label>
            <label className="field">
              <span>Statement Ending Balance</span>
              <input
                inputMode="decimal"
                value={balanceText}
                onChange={(event) => {
                  setError(null);
                  setBalanceText(event.target.value);
                }}
              />
            </label>
          </div>
          {account.normalBalance === 'credit' && (
            <p className="muted">Type the balance owed, as the card statement shows it.</p>
          )}
          {balanceInvalid && <p className="error">The ending balance is not a number of dollars.</p>}

          {/*
            The statement the balance is typed from, for this account and date.
            Offered once a date is picked, because a statement belongs to one.
            Remove is absent while a live reconciliation for the date exists: the
            statement is its evidence, and the database refuses it anyway.
          */}
          {DATE.test(endDate) && (
            <section className="reconcile-statement" aria-label="Bank Statement">
              <h3>Bank Statement</h3>
              {currentStatement ? (
                <p>
                  <button
                    type="button"
                    className="link"
                    onClick={() => void openStatement(currentStatement)}
                  >
                    {currentStatement.originalFilename}
                  </button>
                  {currentStatement.removable && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="link"
                        disabled={busy}
                        aria-label={`Remove ${currentStatement.originalFilename}`}
                        onClick={() => void removeStatement(currentStatement)}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </p>
              ) : (
                <FileDropzone
                  // One statement per reconciliation: `multiple` stays off, so
                  // this is always a batch of one.
                  upload={(files) => uploadStatement(files[0]!)}
                  onError={setError}
                  inputLabel="Attach the bank statement"
                  idleText="Drop the bank statement here, or choose a file"
                  note="PDF or image, up to 20 MB"
                  failureText="Could not attach that statement."
                />
              )}
            </section>
          )}

          {preview && (
            <div className="reconcile-preview">
              {/*
                One live reconciliation per statement date. Any OTHER date is
                open, an EARLIER one included: a reconciliation is cumulative, so
                a month before the latest checks a balance the later one skipped
                over (0051). Until 2026-09-24 this notice fired for every date on
                or before the latest and told the operator to pick a later one,
                which left the first two months of the books unreconcilable
                short of undoing eight.
              */}
              {preview.alreadyReconciled && (
                <p className="notice notice-warn">
                  {account.code} is already reconciled through {preview.through}. A statement date
                  is reconciled once; undo that reconciliation to record it again.
                </p>
              )}
              {/*
                The dead end, stated. Before the latest reconciliation every
                entry is frozen, so a difference here cannot be corrected from
                any screen until the later reconciliations are undone. Only
                shown while there IS a difference: at zero it is noise.
              */}
              {!preview.alreadyReconciled &&
                account.reconciledThrough !== null &&
                preview.through < account.reconciledThrough &&
                difference !== null &&
                difference !== 0 && (
                  <p className="notice notice-warn">
                    {account.code} is reconciled through {account.reconciledThrough}, so every entry
                    through {preview.through} is frozen. To correct this difference, undo the
                    reconciliations after {preview.through}, newest first.
                  </p>
                )}

              <dl className="facts">
                <div className="fact">
                  <dt>Statement</dt>
                  <dd>
                    {preview.statementBalanceCents === null
                      ? '—'
                      : show(preview.statementBalanceCents)}
                  </dd>
                </div>
                <div className="fact">
                  <dt>Bank Feed</dt>
                  <dd>{preview.feedBalanceCents === null ? '—' : show(preview.feedBalanceCents)}</dd>
                </div>
                <div className="fact">
                  <dt>Books</dt>
                  <dd>{show(preview.bookBalanceCents)}</dd>
                </div>
                <div className="fact">
                  <dt>Difference</dt>
                  <dd
                    className={
                      difference === 0 ? 'reconcile-balanced' : difference === null ? '' : 'rail-off'
                    }
                  >
                    {difference === null ? '—' : difference === 0 ? 'Balanced' : show(difference)}
                  </dd>
                </div>
              </dl>

              {/*
                WHAT THE DIFFERENCE IS MADE OF, above the lists rather than
                below them: it is the sentence that decides whether the lists
                are worth reading at all.
              */}
              <DifferenceExplained preview={preview} show={show} />

              {feedGap !== null && feedGap !== 0 && (
                <p className="notice notice-warn">
                  The bank feed is {formatCents(Math.abs(toDisplay(feedGap, account)))} away from the
                  statement. A transaction may be missing from the sync, or still pending at the
                  bank.
                </p>
              )}

              <button
                type="button"
                disabled={busy || difference !== 0 || preview.alreadyReconciled}
                onClick={() => void markReconciled()}
              >
                Mark Reconciled
              </button>

              {preview.unbooked.length > 0 && (
                <section className="reconcile-list" aria-label="In the Bank, Not in the Books">
                  <h3>In the Bank, Not in the Books</h3>
                  <table className="lines">
                    <tbody>
                      {preview.unbooked.map((row) => (
                        <tr key={row.headerId}>
                          <td className="date-cell">{row.date}</td>
                          <td>
                            <Link to={`/journals/${row.headerId}`}>
                              {row.counterparty ?? 'No counterparty'}
                            </Link>
                          </td>
                          <td>
                            <span className="tag">
                              {row.transferPending
                                ? 'Transfer, Other Side Not Posted'
                                : row.status === 'dismissed'
                                  ? 'Excluded'
                                  : row.uncoded
                                    ? 'Uncategorized'
                                    : 'Draft'}
                            </span>
                          </td>
                          <td className="num">{show(row.bankAmountCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {preview.offFeed.length > 0 && (
                <section className="reconcile-list" aria-label="In the Books, Not From the Bank Feed">
                  <h3>In the Books, Not From the Bank Feed</h3>
                  <table className="lines">
                    <tbody>
                      {preview.offFeed.map((row) => (
                        <tr key={row.headerId}>
                          <td className="date-cell">{row.date}</td>
                          <td>
                            <Link to={`/journals/${row.headerId}`}>
                              {row.counterparty ?? row.memo ?? 'Entry'}
                            </Link>
                          </td>
                          <td className="num">{show(row.amountCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </div>
          )}
        </SideDrawer>
      )}
    </section>
  );
}
